import { cn } from '@rocco/utils/cn';
import type { TeamMember, TeamMemberDetail } from './types';
import { BASE_STYLE } from './style';
import { useMemo } from 'react';
import { normalizeCategory, formatCategoryName } from './utils';
import { useTracker } from '../../../../../../tracker/src/react-bindings';

/**
 * Side navigation component for selecting different professional categories in the project team
 */

interface ProjectTeamSideNavProps {
    teamList: string[];
    activeTab: string;
    setActiveTab: (tab: string) => void;
    setCurrentIndex: (index: number) => void;
    teamData: TeamMember[];
    currentTeamMember: TeamMemberDetail;
}

export const ProjectTeamSideNav = ({
    teamList,
    activeTab,
    setActiveTab,
    setCurrentIndex,
    teamData,
}: ProjectTeamSideNavProps) => {
    const tracker = useTracker();
    // Display only categories that definitely exist
    const availableTeams = useMemo(() => {
        // If a pre-filtered list is provided
        if (teamList && teamList.length > 0) {
            return teamList;
        }

        // Filter categories directly
        const categoriesWithMembers = teamData
            .filter((category) => {
                // Select only categories with public members
                return (
                    category.TeamMembers &&
                    category.TeamMembers.some((member) => {
                        return member.IsPublic !== false;
                    })
                );
            })
            .map((category) => category.Category);

        return categoriesWithMembers;
    }, [teamList, teamData]);

    // Don't render if category list is empty
    if (!availableTeams || availableTeams.length === 0) {
        return null;
    }

    return (
        <div className={BASE_STYLE.SIDEBAR_WIDTH}>
            <ul className="w-fit">
                {availableTeams.map((team) => (
                    <li key={team} className="mb-1 w-full text-black">
                        <button
                            onClick={() => {
                                setActiveTab(team);
                                setCurrentIndex(0);
                                tracker.log('ProjectTeamClick', {
                                    url: new URL(window.location.href),
                                    data: {
                                        Category: team,
                                    },
                                });
                            }}
                            className={cn(
                                'relative w-fit cursor-pointer border-0 bg-transparent pb-0.5 text-left text-base text-lg font-normal',
                                normalizeCategory(team) ===
                                    normalizeCategory(activeTab)
                                    ? 'border-b-2 border-black'
                                    : 'border-b-0 text-gray-500'
                            )}
                        >
                            {formatCategoryName(team)}
                        </button>
                    </li>
                ))}
            </ul>
        </div>
    );
};
